import React from "react";
import { ArrowUpRight, Network, Globe2, Package } from "lucide-react";
import Bolt from "@/components/Bolt";
import { useT } from "@/lib/i18n";

export default function Ecosystem() {
  const { t } = useT();
  return (
    <section
      data-testid="ecosystem-section"
      className="relative py-24 sm:py-28 zp-band-cyan border-y border-[color:var(--zp-border)] overflow-hidden"
    >
      <div className="absolute inset-0 zp-grid-bg opacity-30 pointer-events-none" />
      <Bolt
        size={400}
        color="#0A2540"
        className="absolute -right-16 -bottom-20 pointer-events-none"
        style={{ opacity: 0.06, transform: "rotate(12deg)" }}
      />
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="max-w-2xl mb-14">
          <div className="zp-label">{t("eco.eyebrow")}</div>
          <h2 className="font-display text-3xl sm:text-5xl tracking-tighter mt-3">
            {t("eco.title.a")}{" "}
            <span className="text-[color:var(--zp-text-3)]">{t("eco.title.b")}</span>
          </h2>
          <p className="text-[color:var(--zp-text-2)] mt-5 leading-relaxed">
            {t("eco.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          <BrandCard
            testId="ecosystem-zopran"
            colSpan="md:col-span-8 md:row-span-2"
            primary
            icon={Network}
            badge="Main Brand · Germany"
            name="Zopran GmbH"
            location="Darmstadt · Frankfurt Hub"
            description="The German operations arm. EU customs, DDP, IOR, exhibition logistics, FBA imports, aerospace & automotive white-glove and bonded warehousing — orchestrated from our Frankfurt hub."
            tags={["EU Customs", "DDP & IOR", "FBA Germany", "Aerospace WG"]}
            href="https://www.zopran.com"
          />
          <BrandCard
            testId="ecosystem-aeroship"
            colSpan="md:col-span-4"
            icon={Globe2}
            badge="Sub Brand · Estonia"
            name="AeroShip Global OÜ"
            location="Tallinn · Global Backend"
            description="Global freight backbone — air consolidations, multimodal routing and worldwide partner orchestration."
            tags={["Air Freight", "Multimodal"]}
            href="https://www.aeroship-global.com"
          />
          <BrandCard
            testId="ecosystem-smp"
            colSpan="md:col-span-4"
            icon={Package}
            badge="Sub Brand · E-Commerce"
            name="ShipMyParcels.com"
            location="Cross-border Parcels"
            description="Parcel & e-commerce product, built for high-volume DDP cross-border shipping with branded tracking and returns."
            tags={["DDP Parcels", "Returns"]}
            href="https://www.shipmyparcels.com"
          />
        </div>
      </div>
    </section>
  );
}

function BrandCard({ testId, colSpan, primary, icon: Icon, badge, name, location, description, tags, href }) {
  return (
    <a
      data-testid={testId}
      href={href}
      target="_blank"
      rel="noreferrer"
      className={`${colSpan} relative zp-card p-7 sm:p-9 overflow-hidden group block`}
    >
      {primary && (
        <>
          <div className="absolute inset-0 bg-gradient-to-br from-[#5CE1E6]/20 via-transparent to-transparent" />
          <Bolt
            size={260}
            color="#5CE1E6"
            className="absolute -top-10 -right-10 pointer-events-none"
            style={{ opacity: 0.18, transform: "rotate(-12deg)" }}
          />
        </>
      )}
      <div className="relative flex flex-col h-full">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[color:var(--zp-bg-2)] grid place-items-center text-[color:var(--zp-cyan-3)]">
              <Icon size={18} strokeWidth={1.7} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--zp-text-3)] font-semibold">
                {badge}
              </div>
              <h3 className={`font-display tracking-tight ${primary ? "text-3xl sm:text-4xl" : "text-xl"} mt-1`}>
                {name}
              </h3>
            </div>
          </div>
          <ArrowUpRight size={18} className="text-[color:var(--zp-text-3)] group-hover:text-[color:var(--zp-navy)] transition" />
        </div>
        <div className="text-xs text-[color:var(--zp-text-3)] mt-1">{location}</div>
        <p className={`text-[color:var(--zp-text-2)] mt-5 leading-relaxed ${primary ? "text-base max-w-xl" : "text-sm"}`}>
          {description}
        </p>
        <div className="mt-auto pt-6 flex flex-wrap gap-2">
          {tags.map((tag) => (
            <span
              key={tag}
              className="text-[11px] px-2.5 py-1 rounded-full border border-[color:var(--zp-border)] bg-white text-[color:var(--zp-text-2)] font-medium"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </a>
  );
}
